import getEnvironDomain from "ati-utils/getEnvironDomain";
import declension from "ati-core-utils/declension";
import { makeAutoObservable } from "mobx";
import { emailErrorMessage } from "../../utils/emailValidate";
import { roles } from "../../const/index";
import { limitSeparate } from "../../utils/limitSeparate";
import api from "../../api";
import { IGlobalStore } from "./Global.types";
import { IRootStore } from "../index";
import addMetric from "../../utils/addMetric";
import i18nStings from "../../strings/stings";
import Metrics from "../../const/Metrics";

class GlobalStore implements IGlobalStore {
  rootStore: IRootStore;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.initialize();
    makeAutoObservable(this);
  }
  strings = i18nStings;

  firmName = "";
  amount = "";
  role = 0;
  hasActiveRequest = false;
  showAdvancePopup = false;
  showSendPopupSuccess = false;
  showSendPopupUnsuccess = false;
  limit = 0;
  email = "";
  contactID = 0;
  ownership = "";
  loadedLoans = false;

  touched = {
    email: false,
    amount: false,
  };
  error = {
    email: false,
    amount: false,
  };
  errorMessage = {
    email: "",
    amount: "",
  };

  initialize = async () => {
    this.postPostLoansView();
    await this.getLoanRequest();
    await this.getAccountRequest();
  };

  postPostLoansView = () => {
    api
      .postLoansView({})
      .catch(() => {
        this.rootStore.notificationModule.setNotification({
          type: "error",
          message: "Во время загрузки страницы произошла ошибка",
        });
      });
  };

  getLoanRequest = async () => api
      .getLoans()
      .then(({ data }) => {
        this.limit = data.limit;
        this.role = data.role;
        this.hasActiveRequest = data.has_active_request;
        if (this.role === roles.carrier) {
          addMetric(Metrics.bk_page_carrier);
          addMetric(Metrics.BridgeCapitalLoansCarrierLanding, Metrics.product);
        }
        if (this.role === roles.shipper) {
          addMetric(Metrics.bk_page_shipper);
          addMetric(Metrics.BridgeCapitalLoansForwarderLanding, Metrics.product);
        }
        if (this.role === roles.topShipper) {
          addMetric(Metrics.bk_page_top_shipper);
          addMetric(Metrics.BridgeCapitalLoansTopForwarderLanding, Metrics.product);
        }
        if (this.role === roles.fuel) {
          addMetric(Metrics.bk_page_fuel);
          addMetric(Metrics.BridgeCapitalLoansFuelLanding, Metrics.product);
        }
        if (data.limit === 0) {
          window.location.href = `//${getEnvironDomain()}/partner-page/bridgecapital`;
        }
      })
      .catch((e) => {
        if (e.response?.status !== 401) {
          this.rootStore.notificationModule.setNotification({
            type: "error",
            message: e.response?.data?.reason,
          });
        }
        if (e.response?.status === 400) {
          window.location.href = `//${getEnvironDomain()}`;
        }
      });
  getAccountRequest = async () => api
      .getAccount()
      .then(({ data }) => {
        this.contactID = data.contact_id;
        this.email = data.contacts.find((contact) => contact?.id === this.contactID)?.email || "";
        this.firmName = data.firm_name;
        this.ownership = data.ownership;
      })
      .catch((e) => {
        if (e.response?.status !== 401) {
          this.rootStore.notificationModule.setNotification({
            type: "error",
            message: e.response?.data?.reason,
          });
        }
      })
      .finally(() => {
        this.loadedLoans = true;
      });

  postLoanRequest = (formData) => {
    api
      .postLoans(formData)
      .then(() => {
        this.showSendPopupSuccess = true;
        this.showAdvance(false);
        addMetric(Metrics.bk_request_success);
      })
      .catch((error) => {
        this.rootStore.notificationModule.setNotification({
          type: "error",
          message: error?.response?.data?.reason,
        });
      });
  };

  handleChange = (value, name) => {
    this[name] = value;
    if (name === "amount") {
      this[name] = value.replace(/[^0-9\-()]/g, "");
    }
  };

  handleBlur = (event) => {
    const { name } = event.target;
    this.touched[name] = true;
    this.validateForm();
    if (name === "amount") {
      addMetric(Metrics.bk_enter_amount);
    }
    if (name === "email") {
      addMetric(Metrics.bk_enter_email);
    }
  };

  validateAmount = () => {
    if (
      (!this.amount && this.touched.amount)
      || (this.amount && Number(this.amount) > this.limit)
      || (this.amount && Number(this.amount) === 0)
      || !this.amount
    ) {
      this.error.amount = true;
      this.errorMessage.amount = `${
        this.strings.specifyTheAmount
      }${limitSeparate(this.limit)} ${declension(
        this.limit,
        this.strings.rubl,
      )}`;
      return false;
    }
    this.error.amount = false;
    this.errorMessage.amount = "";
    return true;
  };

  validateEmail = async () => {
    const re =      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Zа-яА-ЯёЁ\-0-9]+\.)+[a-zA-Zа-яА-ЯёЁ]{2,}))$/; //eslint-disable-line
    if (
      (!this.email && this.touched.email)
      || (!re.test(this.email) && this.email)
    ) {
      this.error.email = true;
      this.errorMessage.email = this.strings.enterTheEmail;
      return false;
    }
    if (re.test(this.email)) {
      this.errorMessage.email = await emailErrorMessage(this.email);
      this.error.email = !!this.errorMessage.email;
      return !this.errorMessage.email;
    }
    this.error.email = false;
    return true;
  };

  validateForm = async () => {
    const validEmail = await this.validateEmail();
    const validAmount = this.validateAmount();
    if (validAmount && validEmail) return true;
    return false;
  };

  showAdvance = (value) => {
    this.showAdvancePopup = value;
    if (value) {
      addMetric(Metrics.bk_get_prepayment);
    }
  };

  showSend = async (value, name) => {
    if (name !== "success") {
      const isValid = name === "advance" && (await this.validateForm());
      if (isValid && !this.hasActiveRequest) {
        this.getServerData();
      }
      if (this.hasActiveRequest) {
        this.showSendPopupUnsuccess = value;
        if (value) {
          addMetric(Metrics.bk_get_prepayment);
        }
      }
    }
    if (!this.hasActiveRequest && name === "success") {
      this.hasActiveRequest = true;
      this.showSendPopupSuccess = value;
    }
  };

  getServerData = () => {
    const result = this.prepareFormForServer();
    this.postLoanRequest(result);

    return result;
  };

  prepareFormForServer = () => ({
    amount: Number(this.amount),
    email: this.email,
  });
}

export default GlobalStore;
